import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/i18n/LanguageContext";

// Cafeo Moment — a cinematic art photograph (three cans among coffee cherries
// on a stone slab, golden-hour rainforest light) paired with an editorial
// caption. Sits between CafeoHero and CafeoMoods on the Cafeo brand page.
// -----------------------------------------------------------------------------
const COCOA = "#2A1B0F";
const GOLD = "#F4C169";
const CREAM = "#F7EBD5";

export default function CafeoMoment({ image }) {
  const { t } = useT();
  if (!image) return null;
  const isTranslated = (v) => typeof v === "string" && !v.startsWith("brandContent.");

  const overlineRaw = t("brandContent.cafeo.moment.overline");
  const overline = isTranslated(overlineRaw) ? overlineRaw : "From bean to can";
  const titleRaw = t("brandContent.cafeo.moment.title");
  const title = isTranslated(titleRaw)
    ? titleRaw
    : "Brewed slow. Never rushed.";
  const bodyRaw = t("brandContent.cafeo.moment.body");
  const body = isTranslated(bodyRaw)
    ? bodyRaw
    : "100% Arabica beans, brewed the way a barista would — then chilled and canned so the ritual holds. Three moods on a stone slab: silky Dark Chocolate Truffle, warm Dulce de Leche, bracing Dark Americano.";
  const tagRaw = t("brandContent.cafeo.moment.tag");
  const tag = isTranslated(tagRaw) ? tagRaw : "Cafeo · Brewed Iced Coffee";

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: COCOA, color: CREAM }}
      data-testid="cafeo-moment"
    >
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT — editorial caption */}
          <div className="lg:col-span-4">
            <p
              className="text-[0.7rem] md:text-xs font-bold uppercase mb-5"
              style={{ color: GOLD, letterSpacing: "0.36em" }}
            >
              {overline}
            </p>
            <h2
              className="font-editorial italic leading-[1.05] text-[clamp(2.25rem,4.4vw,3.75rem)]"
              style={{ color: CREAM }}
            >
              {title}
            </h2>
            <div
              className="mt-6 h-px w-24"
              style={{ background: `linear-gradient(90deg, ${GOLD}, transparent)` }}
            />
            <p
              className="mt-8 text-base md:text-lg leading-[1.75] max-w-md"
              style={{ color: `${CREAM}cc` }}
            >
              {body}
            </p>
            <p
              className="mt-8 text-xs font-bold uppercase"
              style={{ color: GOLD, letterSpacing: "0.3em" }}
            >
              {tag}
            </p>
          </div>

          {/* RIGHT — full cinematic photograph */}
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.98 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden rounded-3xl shadow-[0_35px_90px_-25px_rgba(0,0,0,0.6)]"
              style={{ border: `1px solid ${GOLD}33` }}
            >
              <img
                src={image}
                alt="Cafeo — three iced-coffee cans on a stone slab among coffee cherries at golden hour"
                data-testid="cafeo-moment-image"
                className="w-full h-auto block"
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Fine bottom divider */}
      <div
        className="h-[3px] w-full"
        style={{
          background: `linear-gradient(90deg, transparent 5%, ${GOLD} 20%, ${GOLD} 80%, transparent 95%)`,
        }}
      />
    </section>
  );
}
